import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CreditCard,
  Info,
  LoaderCircle,
  LockKeyhole,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import type { AvailableNumberSearchResult } from "../signup-prototype/availableNumbers";
import { usStatesShortCodes } from "../utils/address/usStatesShortCodes";
import { caProvincesShortCodes } from "../utils/address/caProvincesShortCodes";
import { VerificationCard } from "./VerificationCard";
import { SignupSupport } from "./SignupSupport";
import { HostedNumberHelp } from "./HostedNumberHelp";
import { RegistrationFeedback } from "./RegistrationFeedback";
import formatPhoneForUser from "../../utils/formatPhoneForUser";
import { formatVerificationPhone, readVerificationPhone, phoneCaret, verificationPhoneForComparison } from "./verificationPhone";
import { beginDraft, reconcileDraft, resolveDraft } from "./verificationDraft";
import { normalizeWebsiteInput } from "../../utils/websiteAddress";
import { isTouchViewport } from "../../hooks/use-mobile";
import {
  BUSINESS_INDUSTRIES,
  BUSINESS_TYPES,
  JOB_POSITIONS,
  labelForIndustry,
} from "./verificationFields";
import {
  formatOffer,
  messageAllowance,
  type VerificationApi,
  type VerificationDraft,
  type VerificationSnapshot,
  type PaymentSetup,
} from "./verificationApi";
import "../signup-prototype/verification.css";
import "./verification.css";

type Step = "payment" | "business" | "number" | "review";
const steps: Array<{ key: string; step: Step; page?: number; label: string }> = [
  { key: "payment", step: "payment", label: "Card" },
  { key: "number", step: "number", label: "Number" },
  { key: "business", step: "business", page: 0, label: "Business" },
  { key: "contact", step: "business", page: 1, label: "Contact" },
  { key: "address", step: "business", page: 2, label: "Address" },
  { key: "review", step: "review", label: "Review" },
];
const businessPageFields = [
  ["businessName", "businessType", "businessIndustry", "businessOwnership", "businessRegistrationNumber", "websiteUrl"],
  ["firstName", "lastName", "email", "phoneNumber", "businessTitle", "jobPosition"],
  ["streetAddress", "city", "region", "postalCode"],
];
const fieldLabels: Record<string, string> = {
  businessName: "Legal business name", businessType: "Business type", businessIndustry: "Business industry",
  businessOwnership: "Business ownership", businessRegistrationNumber: "Business registration number",
  websiteUrl: "Business website", firstName: "First name", lastName: "Last name", email: "Contact email",
  phoneNumber: "Contact phone number", businessTitle: "Job title", jobPosition: "Role",
  streetAddress: "Business street address", city: "City", region: "State or province", postalCode: "Postal code",
  termsOfServiceUrl: "SMS terms link", privacyPolicyUrl: "Privacy policy link", number: "Texting number",
};
interface Props {
  snapshot: VerificationSnapshot;
  api: VerificationApi;
  busy: boolean;
  error: string;
  fieldErrors?: Record<string, string>;
  perform: (work: () => Promise<any>) => Promise<any>;
  onClose: () => void;
  onSubmitted?: () => void;
}
export default function LiveVerificationWizard({
  snapshot,
  api,
  busy,
  error,
  fieldErrors,
  perform,
  onClose,
  onSubmitted,
}: Props) {
  const correctionPage = businessPageFields.findIndex(fields => fields.some(field => snapshot.registration.correction?.fields.includes(field)));
  const [step, setStep] = useState<Step>(
    snapshot.card.status !== "saved" ? "payment"
      : snapshot.registration.correction?.status === "editing" ? (correctionPage < 0 ? "review" : "business")
      : snapshot.number.assigned ? "business" : "number",
  );
  const [businessPage, setBusinessPage] = useState(() => snapshot.registration.correction?.status === "editing"
    ? Math.max(0, businessPageFields.findIndex(fields => fields.some(field => snapshot.registration.correction!.fields.includes(field)))) : 0);
  const [editing, setEditing] = useState(() => beginDraft(snapshot.draft));
  const draft = editing.values;
  const submittedDraft = useRef<{ entered: VerificationDraft; normalized: VerificationDraft } | undefined>(undefined);
  const [phoneInputHint, setPhoneInputHint] = useState("");
  const phoneSelection = useRef<{ input: HTMLInputElement; position: number } | undefined>(undefined);
  useLayoutEffect(() => {
    const selection = phoneSelection.current;
    phoneSelection.current = undefined;
    if (selection && document.activeElement === selection.input)
      selection.input.setSelectionRange(selection.position, selection.position);
  });
  const [accepted, setAccepted] = useState(false);
  const [representative, setRepresentative] = useState(false);
  const [areaCode, setAreaCode] = useState(snapshot.business.areaCode || "");
  const [numbers, setNumbers] = useState<AvailableNumberSearchResult | null>(
    null,
  );
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [numberEditing, setNumberEditing] = useState(() => beginDraft({ number: snapshot.number.selected || "" }));
  const selected = numberEditing.values.number;
  const setSelected = (number: string) => setNumberEditing(current => ({ ...current, values: { number } }));
  const conflicts = { ...editing.conflicts, ...numberEditing.conflicts };
  const hasConflicts = Object.keys(conflicts).length > 0;
  const search = useRef<{ id: number; controller?: AbortController }>({
    id: 0,
  });
  const titleRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const canada = snapshot.business.country === "CA";
  const staging = snapshot.environment === "staging";
  const offer = snapshot.offer;
  const correction = snapshot.registration.correction;
  const correcting = correction?.status === "editing";
  const assistedCorrection = correcting && correction.mode === "assisted";
  const editablePages = businessPageFields.map((fields, page) => ({ page, editable: !correcting || fields.some(field => correction.fields.includes(field)) })).filter(item => item.editable).map(item => item.page);
  const fieldLocked = (key: string) => correcting && !correction.fields.includes(key);
  const ownershipAllowed =
    draft.businessOwnership === "privately_held" ||
    (draft.businessOwnership === "nonprofit_or_government" &&
      ["NOT_FOR_PROFIT", "GOVERNMENT"].includes(draft.businessIndustry));
  const index = steps.findIndex((item) => item.step === step && (step !== "business" || item.page === businessPage));
  useEffect(() => {
    const submitted = submittedDraft.current;
    submittedDraft.current = undefined;
    setEditing(current => reconcileDraft(current, snapshot.draft, submitted));
    setNumberEditing(current => reconcileDraft(current, { number: snapshot.number.selected || "" }));
    setAccepted(false);
    setRepresentative(false);
    if (snapshot.card.status !== "saved") setStep("payment");
  }, [snapshot.revision, snapshot.draft, snapshot.number.selected, snapshot.number.assigned, snapshot.card.status, snapshot.card.brand, snapshot.card.last4, snapshot.offer?.version]);
  useEffect(() => {
    if (!error && !hasConflicts) return;
    const frame = requestAnimationFrame(() => {
      contentRef.current?.scrollTo({ top: 0 });
      titleRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [error, hasConflicts]);
  useEffect(
    () => () => {
      ++search.current.id;
      search.current.controller?.abort();
    },
    [],
  );
  // Rotating a phone or tablet, or the keyboard opening, resizes the sheet.
  // Keep the field being filled in on screen rather than under the fold or
  // behind the pinned Back / Continue bar.
  useEffect(() => {
    const vv = window.visualViewport;
    let frame = 0;
    const reveal = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const active = document.activeElement;
        if (!isTouchViewport() || !(active instanceof HTMLElement)) return;
        if (!active.matches("input, select, textarea") || !contentRef.current?.contains(active)) return;
        active.scrollIntoView({ block: "nearest" });
      });
    };
    vv?.addEventListener("resize", reveal);
    window.addEventListener("resize", reveal);
    return () => {
      cancelAnimationFrame(frame);
      vv?.removeEventListener("resize", reveal);
      window.removeEventListener("resize", reveal);
    };
  }, []);
  const navigate = (next: Step, page = 0) => {
    setStep(next);
    if (next === "business") setBusinessPage(page);
    contentRef.current?.scrollTo({ top: 0 });
    requestAnimationFrame(() => titleRef.current?.focus());
  };
  const update = (key: string, value: string) => {
    setEditing(current => ({ ...current, values: { ...current.values, [key]: value } }));
    setAccepted(false);
    setRepresentative(false);
  };
  const field = (
    key: string,
    label: string,
    options: {
      type?: string;
      max?: number;
      auto?: string;
      help?: string;
      pattern?: string;
      /** The phone keyboard for digit-only fields. */
      numeric?: boolean;
    } = {},
  ) => (
    <label className={`sv-field ${snapshot.registration.rejection?.fields.includes(key) ? "sv-field-correction" : ""}`}>
      <span id={`label-${key}`}>{label}</span>
      <input
        required
        disabled={fieldLocked(key)}
        type={options.type === "url" ? "text" : options.type || "text"}
        inputMode={options.numeric ? "numeric" : options.type === "url" ? "url" : options.type === "tel" ? "tel" : undefined}
        enterKeyHint="next"
        autoCapitalize={options.type === "url" || options.type === "email" ? "none" : undefined}
        spellCheck={options.type === "url" ? false : undefined}
        placeholder={options.type === "url" ? "yourbusiness.com" : undefined}
        onBlur={options.type === "url" ? event => update(key, normalizeWebsiteInput(event.target.value)) : undefined}
        maxLength={key === "phoneNumber" ? undefined : options.max || 200}
        pattern={options.pattern}
        autoComplete={options.auto || "off"}
        value={key === "phoneNumber" ? formatVerificationPhone(draft[key] || "") : draft[key] || ""}
        onChange={(event) => {
          if (key !== "phoneNumber") { update(key, event.target.value); return; }
          const input = event.currentTarget;
          const raw = input.value;
          const before = raw.slice(0, input.selectionStart ?? raw.length);
          const result = readVerificationPhone(raw);
          setPhoneInputHint(result.error || "");
          if (result.error) return;
          phoneSelection.current = { input, position: phoneCaret(result.value!, before.replace(/\D/g, "").length, before.startsWith("+")) };
          update(key, result.value!);
        }}
        aria-labelledby={`label-${key}`}
        aria-invalid={!!fieldErrors?.[key]}
        aria-describedby={
          [options.help && `help-${key}`, fieldErrors?.[key] && `error-${key}`, key === "phoneNumber" && phoneInputHint && "phone-input-hint"]
            .filter(Boolean)
            .join(" ") || undefined
        }
      />
      {key === "phoneNumber" && phoneInputHint && <small id="phone-input-hint" className="sv-field-error" role="status">{phoneInputHint}</small>}
      {options.help && (
        <small id={`help-${key}`} className="sv-helper">
          {options.help}
        </small>
      )}
      {fieldErrors?.[key] && (
        <small id={`error-${key}`} className="sv-field-error">
          {fieldErrors[key]}
        </small>
      )}
    </label>
  );
  const select = (
    key: string,
    label: string,
    options: string[],
    format = (value: string) => value,
  ) => (
    <label className={`sv-field ${snapshot.registration.rejection?.fields.includes(key) ? "sv-field-correction" : ""}`}>
      <span id={`label-${key}`}>{label}</span>
      <select
        required
        disabled={fieldLocked(key)}
        value={draft[key] || ""}
        onChange={(event) => update(key, event.target.value)}
        aria-labelledby={`label-${key}`}
        aria-invalid={!!fieldErrors?.[key]}
        aria-describedby={fieldErrors?.[key] ? `error-${key}` : undefined}
      >
        <option value="">Choose one</option>
        {options.map((value) => (
          <option key={value} value={value}>
            {format(value)}
          </option>
        ))}
      </select>
      {fieldErrors?.[key] && (
        <small id={`error-${key}`} className="sv-field-error">
          {fieldErrors[key]}
        </small>
      )}
    </label>
  );
  const findNumbers = async () => {
    search.current.controller?.abort();
    const id = ++search.current.id;
    setNumbers(null);
    setSearchError("");
    if (areaCode && !/^[2-9][0-9]{2}$/.test(areaCode)) {
      setSearchError("Enter a three-digit area code, or leave it blank.");
      return;
    }
    const controller = new AbortController();
    search.current.controller = controller;
    setSearching(true);
    try {
      const result = await api.numbers(
        snapshot.business.country,
        areaCode,
        controller.signal,
      );
      if (id === search.current.id) setNumbers(result);
    } catch (reason) {
      if (id === search.current.id && !controller.signal.aborted)
        setSearchError(
          reason instanceof Error
            ? reason.message
            : "We couldn’t load numbers. Please try again.",
        );
    } finally {
      if (id === search.current.id) setSearching(false);
    }
  };
  const continueStep = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || hasConflicts) return;
    if (step === "business") {
      if (!ownershipAllowed) return;
      const submitted = {
          ...Object.fromEntries(Object.entries(draft).filter(([key]) => !["termsOfServiceUrl", "privacyPolicyUrl"].includes(key))),
          ...Object.fromEntries(["websiteUrl"]
            .filter(key => typeof draft[key] === "string")
            .map(key => [key, normalizeWebsiteInput(draft[key])])),
          country: snapshot.business.country,
          businessRegistrationIdentifier: canada ? "CCN" : "EIN",
      };
      submittedDraft.current = { entered: draft, normalized: { ...submitted, ...(draft.phoneNumber ? { phoneNumber: verificationPhoneForComparison(draft.phoneNumber) } : {}) } };
      const result = await perform(async () => {
        const saved = await api.save(snapshot.revision, submitted);
        // Acknowledge our own canonical response before the parent applies it.
        if ("version" in saved)
          setEditing(current => reconcileDraft({ ...current, baseline: draft }, saved.draft));
        submittedDraft.current = undefined;
        return saved;
      });
      if (!result) return;
      const nextPage = editablePages.find(page => page > businessPage);
      if (nextPage !== undefined) navigate("business", nextPage);
      else navigate("review");
    } else if (step === "number" && (selected || snapshot.number.assigned)) {
      if (!snapshot.number.assigned) {
        const result = await perform(() => api.selectNumber(snapshot.revision, selected));
        if (!result) return;
        if (!result.number?.assigned) {
          setSearchError("Your number is still being reserved. Please try continuing again in a moment.");
          return;
        }
      }
      navigate("business");
    } else if (step === "review" && offer && accepted && representative) {
      const result = await perform(() => api.submit(snapshot.revision, offer.version, undefined, correcting ? correction.generation : undefined));
      if (result && !correcting && ["submitted", "working", "pending_approval", "payment_required", "active"].includes(result.state)) onSubmitted?.();
    }
  };
  const descriptions: Record<Step, string> = staging ? {
    payment: "Add a Stripe test card to try setup. No real card will be charged.",
    business: "Try the business details step. Nothing is submitted to carriers.",
    number: "Choose a fictional number for your staging account. It cannot send or receive texts.",
    review: "Review your test setup. Approval is simulated, then a Stripe test payment completes activation.",
  } : {
    payment:
      "Start by adding your payment method. Your subscription starts only when texting is approved and ready.",
    business:
      "We’ll guide you through the details carriers need to verify your business.",
    number:
      "Choose a dedicated number that customers can use to reach your business.",
    review:
      "Confirm your details and plan. We’ll keep you updated while your registration is reviewed.",
  };
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="sv-overlay" />
        <Dialog.Content
          className="signup-prototype sv-modal live-verification"
          ref={contentRef}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            titleRef.current?.focus();
          }}
          onEscapeKeyDown={(event) => {
            if (busy) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (busy) event.preventDefault();
          }}
        >
          <Dialog.Close
            className="sv-close"
            aria-label="Close verification"
            disabled={busy}
          >
            <X size={18} />
          </Dialog.Close>
          <div className="sv-main">
            <header className="sv-header">
              <Dialog.Title ref={titleRef} tabIndex={-1}>
                Verify your business for texting
              </Dialog.Title>
              <Dialog.Description>{descriptions[step]}</Dialog.Description>
            </header>
            {staging && <aside className="signup-staging-notice" aria-label="Staging test mode"><strong>Staging test mode</strong><span>Simulated carrier approval. No real charges or customer texts.</span></aside>}
            <p className="sv-current-step" aria-live="polite">Step {index + 1} of {steps.length} <strong>{steps[index].label}</strong></p>
            <nav className="sv-progress" aria-label="Verification steps">
              {steps.map((item, i) => (
                <button
                  type="button"
                  key={item.key}
                  className={`sv-progress-item ${i <= index || (correcting && item.step === "number") ? "sv-progress-reached" : ""}`}
                  aria-current={i === index ? "step" : undefined}
                  aria-label={item.label}
                  disabled={
                    busy ||
                    i > index ||
                    (correcting && (["payment", "number"].includes(item.step) || (item.step === "business" && !editablePages.includes(item.page!)))) ||
                    (i > 0 && snapshot.card.status !== "saved")
                  }
                  onClick={() => navigate(item.step, item.page)}
                >
                  <span className="sv-progress-line" />
                  <span className="sv-progress-label">{item.label}</span>
                </button>
              ))}
            </nav>
            <div className="sv-mobile-price">
              <strong>
                $0 today
                {offer && <span> · then {formatOffer(offer)}/month</span>}
              </strong>
              <small>
                {staging ? "Test pricing only. No real charges." : "Billing starts when texting is approved and ready. Plus applicable taxes."}
              </small>
            </div>
            {error && (
              <div className="sv-error" role="alert">
                {error}
                {fieldErrors && (
                  <ul>
                    {Object.entries(fieldErrors).map(([key, value]) => (
                      <li key={key}>{value}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {hasConflicts && (
              <section className="sv-draft-conflicts" aria-label="Review changed details">
                <h3>Choose which details to keep</h3>
                <p>These details were also changed in another window. Your entries are still here. Choose one for each item before continuing.</p>
                {Object.entries(conflicts).map(([key, saved]) => (
                  <div className="sv-draft-conflict" key={key} role="group" aria-label={fieldLabels[key] || key}>
                    <strong>{fieldLabels[key] || key}</strong>
                    <p>Your entry: <span>{key === "number" ? selected : draft[key] || "Blank"}</span></p>
                    <p>Saved entry: <span>{saved || "Blank"}</span></p>
                    <div>
                      <button type="button" disabled={busy} onClick={() => key === "number" ? setNumberEditing(current => resolveDraft(current, key, false)) : setEditing(current => resolveDraft(current, key, false))}>Keep my entry</button>
                      <button type="button" disabled={busy} onClick={() => key === "number" ? setNumberEditing(current => resolveDraft(current, key, true)) : setEditing(current => resolveDraft(current, key, true))}>Use saved entry</button>
                    </div>
                  </div>
                ))}
              </section>
            )}
            {snapshot.registration.rejection ? <RegistrationFeedback registration={snapshot.registration} /> : snapshot.state === "correction_required" && (
              <div className="sv-notice sv-correction-notice"><Info size={17} /><p>{snapshot.registration.message || "Please review and update your business details before submitting again."}</p></div>
            )}
            {correcting && <p className="sv-helper">Your saved card and texting number stay in place. {assistedCorrection ? "Your updates will go to our support team for help with resubmission." : "Review the feedback and update the available fields. Contact support if other details need to change."}</p>}
            {step === "payment" ? (
              <div className="sv-step-body">
                {snapshot.card.status === "saved" && (
                  <div className="sv-saved-card">
                    <CreditCard size={20} />
                    <span>
                      {snapshot.card.brand} ending in {snapshot.card.last4}
                    </span>
                    <button
                      type="button"
                      className="sv-edit"
                      disabled={busy}
                      onClick={() => navigate(snapshot.number.assigned ? "business" : "number")}
                    >
                      Keep this card & continue
                    </button>
                  </div>
                )}
                <VerificationCard
                  staging={staging}
                  prepare={() =>
                    perform(() =>
                      api.setup(snapshot.revision),
                    ) as Promise<PaymentSetup | null>
                  }
                  confirm={(id) =>
                    perform(() =>
                      api.confirm(id),
                    ) as Promise<VerificationSnapshot | null>
                  }
                  onSaved={() => navigate(snapshot.number.assigned ? "business" : "number")}
                />
              </div>
            ) : (
              <form
                className="sv-form"
                onSubmit={(event) => void continueStep(event)}
              >
                <fieldset disabled={busy} className="sv-live-fields">
                  {step === "business" && (
                    <div className="sv-step-body">
                      <div className="sv-section-heading">
                        <h3>
                          {
                            [
                              "Your business",
                              "Your authorized contact",
                              "Business address",
                            ][businessPage]
                          }
                        </h3>

                      </div>
                      {businessPage === 0 && (
                        <>
                          {field("businessName", "Legal business name", {
                            max: 100,
                            auto: "organization",
                          })}
                          <div className="sv-field-row">
                            {select(
                              "businessType",
                              "Business type",
                              BUSINESS_TYPES,
                            )}
                            {select(
                              "businessIndustry",
                              "Business industry",
                              BUSINESS_INDUSTRIES,
                              labelForIndustry,
                            )}
                          </div>
                          {select(
                            "businessOwnership",
                            "Business ownership",
                            [
                              "privately_held",
                              "publicly_traded",
                              "nonprofit_or_government",
                            ],
                            (value) =>
                              ({
                                privately_held: "Privately held",
                                publicly_traded: "Publicly traded",
                                nonprofit_or_government:
                                  "Nonprofit or government",
                              })[value] || value,
                          )}
                          {draft.businessOwnership === "publicly_traded" && (
                            <div className="sv-notice">
                              <Info size={17} />
                              <p>
                                Our team will help register your publicly traded
                                business.{" "}
                                <a href="mailto:support@chekkit.io">
                                  Contact support to continue.
                                </a>
                              </p>
                            </div>
                          )}
                          {draft.businessOwnership ===
                            "nonprofit_or_government" &&
                            !ownershipAllowed && (
                              <p className="sv-helper">
                                Choose Not for profit or Government as your
                                industry to continue with this ownership type.
                              </p>
                            )}
                          {field(
                            "businessRegistrationNumber",
                            canada
                              ? "Business Number (BN)"
                              : "Employer identification number (EIN)",
                            {
                              max: canada ? 9 : 50,
                              pattern: canada
                                ? "[0-9]{9}"
                                : "[0-9]{2}-?[0-9]{7}",
                              numeric: canada,
                              help: canada
                                ? "Enter your nine-digit Canadian Business Number."
                                : "Use the number on your business registration documents.",
                            },
                          )}
                          {field("websiteUrl", "Business website", {
                            type: "url",
                            max: 2048,
                            auto: "url",
                          })}
                          <p className="sv-helper">
                            This setup supports registered, privately held
                            businesses and eligible nonprofit or government
                            organizations. Sole proprietor or missing
                            registration documents?{" "}
                            <a href="mailto:support@chekkit.io">
                              Our team can help.
                            </a>
                          </p>
                        </>
                      )}
                      {businessPage === 1 && (
                        <>
                          <p className="sv-helper">
                            This person must be authorized to register your
                            business for texting.
                          </p>
                          <div className="sv-field-row">
                            {field("firstName", "First name", {
                              max: 50,
                              auto: "given-name",
                            })}
                            {field("lastName", "Last name", {
                              max: 50,
                              auto: "family-name",
                            })}
                          </div>
                          {field("email", "Contact email", {
                            type: "email",
                            max: 254,
                            auto: "email",
                          })}
                          {field("phoneNumber", "Contact phone number", {
                            type: "tel",
                            max: 40,
                            auto: "tel",
                            help: "US and Canadian numbers can be entered with or without +1. For other countries, include the country code.",
                          })}
                          <div className="sv-field-row">
                            {field("businessTitle", "Job title", {
                              max: 100,
                              auto: "organization-title",
                            })}
                            {select("jobPosition", "Role", JOB_POSITIONS)}
                          </div>
                        </>
                      )}
                      {businessPage === 2 && (
                        <>
                          <p className="sv-helper">
                            Use your registered business address in{" "}
                            {canada ? "Canada" : "the United States"}.
                          </p>
                          {field("streetAddress", "Business street address", {
                            max: 200,
                            auto: "street-address",
                          })}
                          {field("city", "City", {
                            max: 100,
                            auto: "address-level2",
                          })}
                          <div className="sv-field-row">
                            {select(
                              "region",
                              canada ? "Province or territory" : "State",
                              canada
                                ? caProvincesShortCodes
                                : usStatesShortCodes,
                            )}
                            {field(
                              "postalCode",
                              canada ? "Postal code" : "ZIP code",
                              { max: 20, auto: "postal-code" },
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {step === "number" && (
                    <div className="sv-step-body">
                      <div className="sv-section-heading">
                        <h3>Choose a texting number</h3>
                        <span>{canada ? "Canada" : "United States"}</span>
                      </div>
                      <HostedNumberHelp business={snapshot.business} reserved={Boolean(snapshot.number.assigned || snapshot.number.locked)} />
                      {snapshot.number.assigned ? (
                        <p className="sv-saved-card">
                          Your number:{" "}
                          <strong>{formatPhoneForUser(snapshot.number.assigned)}</strong>
                        </p>
                      ) : snapshot.number.locked ? (
                        <p className="sv-saved-card">Your chosen number: <strong>{formatPhoneForUser(snapshot.number.selected)}</strong><span>Your request is saved. Continue to finish reserving this number.</span></p>
                      ) : (
                        <>
                          <div className="sv-search-controls">
                            <label className="sv-field">
                              Area code (optional)
                              <input
                                inputMode="numeric"
                                enterKeyHint="search"
                                autoComplete="off"
                                maxLength={3}
                                value={areaCode}
                                placeholder={canada ? "e.g. 204" : "e.g. 720"}
                                onChange={(event) => {
                                  ++search.current.id;
                                  search.current.controller?.abort();
                                  setSearching(false);
                                  setAreaCode(
                                    event.target.value.replace(/\D/g, ""),
                                  );
                                  setNumbers(null);
                                  setSearchError("");
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    void findNumbers();
                                  }
                                }}
                              />
                            </label>
                            <button
                              type="button"
                              className="sv-search-button"
                              disabled={searching}
                              onClick={() => void findNumbers()}
                            >
                              {searching ? (
                                <LoaderCircle
                                  size={17}
                                  className="sv-search-spinner"
                                />
                              ) : (
                                <Search size={17} />
                              )}
                              {searching ? "Searching…" : "Search numbers"}
                            </button>
                          </div>
                          {searchError && (
                            <p className="sv-error" role="alert">
                              {searchError}
                            </p>
                          )}
                          {numbers && numbers.numbers.length === 0 && (
                            <p role="status">
                              No numbers found. Try a nearby area code, or leave
                              it blank.
                            </p>
                          )}
                          {numbers && numbers.numbers.length > 0 && (
                            <fieldset className="sv-number-list">
                              <legend>Available texting numbers</legend>
                              {numbers.numbers.map((number) => (
                                <label
                                  key={number.phoneNumber}
                                  className={
                                    selected === number.phoneNumber
                                      ? "sv-number-selected"
                                      : ""
                                  }
                                >
                                  <input
                                    type="radio"
                                    name="number"
                                    checked={selected === number.phoneNumber}
                                    onChange={() =>
                                      setSelected(number.phoneNumber)
                                    }
                                  />
                                  <span>{formatPhoneForUser(number.phoneNumber)}</span>
                                  <small>
                                    {[number.locality, number.region]
                                      .filter(Boolean)
                                      .join(", ")}
                                  </small>
                                </label>
                              ))}
                            </fieldset>
                          )}
                          {snapshot.number.selected && (
                            <p className="sv-helper">
                              Saved selection: {formatPhoneForUser(snapshot.number.selected)}
                            </p>
                          )}
                          <p className="sv-availability-note">
                            <LockKeyhole size={16} />
                            <span>
                              {staging ? "We’ll save this fictional number for your test account." : "We’ll reserve this number for your business. Your subscription starts only when texting is approved and ready."}
                            </span>
                          </p>
                        </>
                      )}

                    </div>
                  )}
                  {step === "review" && (
                    <div className="sv-step-body">
                      <div className="sv-review-block">
                        <div>
                          <h3>Business</h3>
                          {editablePages.length > 0 && <button
                            type="button"
                            className="sv-edit"
                            onClick={() => navigate("business", editablePages[0])}
                          >
                            Edit
                          </button>}
                        </div>
                        <strong>{snapshot.draft.businessName}</strong>
                        <p>
                          {snapshot.draft.streetAddress}
                          <br />
                          {snapshot.draft.city}, {snapshot.draft.region}{" "}
                          {snapshot.draft.postalCode}
                        </p>
                      </div>
                      <div className="sv-review-block">
                        <div>
                          <h3>Texting number</h3>
                          {!correcting && !snapshot.number.assigned && <button type="button" className="sv-edit" onClick={() => navigate("number")}>Choose</button>}
                        </div>
                        <strong>
                          {formatPhoneForUser(snapshot.number.assigned || snapshot.number.selected)}
                        </strong>
                        <p>
                          {staging ? "This fictional number cannot send or receive texts. Approval is simulated." : "Carrier approval is required before you can send texts."}
                        </p>
                      </div>
                      <div className="sv-review-block">
                        <div>
                          <h3>Payment method</h3>
                          {!correcting && <button type="button" className="sv-edit" onClick={() => navigate("payment")}>Edit</button>}
                        </div>
                        <strong className="sv-review-card">
                          <CreditCard size={18} />
                          {snapshot.card.brand} ending in {snapshot.card.last4}
                        </strong>
                      </div>
                      <p className="sv-helper">Your business’s SMS terms and privacy pages are provided by Chekkit using your texting number. You don’t need to create or enter any links.</p>
                      {offer ? (
                        <>
                          <label className="sv-authorization">
                            <input
                              type="checkbox"
                              required
                              checked={accepted}
                              onChange={(event) =>
                                setAccepted(event.target.checked)
                              }
                            />
                            <span>
                              {staging ? "I agree to test " : "I authorize "}
                              <strong>
                                {offer.name} at {formatOffer(offer)} per month
                              </strong>
                              {staging ? ", using Stripe test mode after simulated approval. No real money is charged." : ", plus applicable taxes, starting automatically when my number is approved and ready to text. Nothing is charged today."}
                              <small>{messageAllowance(offer)}</small>
                            </span>
                          </label>
                          <label className="sv-authorization">
                            <input
                              type="checkbox"
                              required
                              checked={representative}
                              onChange={(event) =>
                                setRepresentative(event.target.checked)
                              }
                            />
                            <span>
                              I’m authorized to register this business, and I’ll
                              only send permitted messages to customers who have
                              agreed to receive them.
                            </span>
                          </label>
                        </>
                      ) : (
                        <p className="sv-error">
                          Your plan is unavailable. Contact our team before
                          continuing.
                        </p>
                      )}
                    </div>
                  )}
                  <footer className="sv-form-footer">
                    <button
                      type="button"
                      className="sv-back"
                      disabled={correcting && (!editablePages.length || (step === "business" && businessPage === editablePages[0]))}
                      onClick={() => {
                        if (correcting) {
                          const previousPage = step === "review" ? editablePages.at(-1) : editablePages.filter(page => page < businessPage).at(-1);
                          if (previousPage !== undefined) navigate("business", previousPage);
                        } else {
                          const previous = steps[index - 1];
                          navigate(previous.step, previous.page);
                        }
                      }}
                    >
                      <ArrowLeft size={16} />
                      Back
                    </button>
                    <button
                      type="submit"
                      className="sv-primary"
                      disabled={
                        busy || hasConflicts ||
                        (step === "business" && !ownershipAllowed) ||
                        (step === "number" &&
                          !selected &&
                          !snapshot.number.assigned) ||
                        (step === "review" &&
                          (!offer || !accepted || !representative))
                      }
                    >
                      {busy
                        ? step === "number" ? "Reserving number…" : "Saving…"
                        : step === "review"
                          ? assistedCorrection ? "Send updates to support" : correcting ? "Resubmit for approval" : staging ? "Submit test setup" : "Submit for approval"
                          : step === "number" && !snapshot.number.assigned ? snapshot.number.locked ? "Continue reserving number" : "Choose number & continue" : "Save & continue"}
                      <ArrowRight size={16} />
                    </button>
                  </footer>
                </fieldset>
              </form>
            )}
            <button type="button" className="sv-back" disabled={busy} onClick={onClose}>Finish later</button>
            <p className="sv-save-note">
              Completed steps are saved. Use Save &amp; continue to save changes before leaving.
              You can return anytime.
            </p>
            <SignupSupport className="sv-support-mobile" />
          </div>
          <aside className="sv-plan" aria-label="Your plan and billing details">
            <span className="sv-plan-eyebrow">{staging ? "YOUR TEST PLAN" : "YOUR PLAN"}</span>
            <div className="sv-today">
              <strong>$0</strong>
              <span>today</span>
            </div>
            {offer ? (
              <>
                <p className="sv-plan-price">
                  {offer.name}
                  <br />
                  Then <strong>{formatOffer(offer)}</strong> / month
                </p>
                <p className="sv-helper sv-tax-note">Plus applicable taxes.</p>
                <p className="sv-plan-price">{messageAllowance(offer)}</p>
                <ul className="sv-plan-features">
                  {offer.features.map((feature) => (
                    <li key={feature}>
                      <Check size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="sv-plan-price">
                Our team can help confirm your plan.
              </p>
            )}
            <div className="sv-billing-promise">
              <ShieldCheck size={21} />
              <p>
                {staging ? <><strong>No real charges.</strong> A Stripe test payment starts after simulated approval.</> : <><strong>Nothing charged today.</strong> Your subscription starts only when your number is approved and ready to text.</>}
              </p>
            </div>
            <p className="sv-activation-note">
              {staging ? "Customer texting stays off, including after test activation." : "Texting becomes available after your first payment succeeds."}
            </p>
            <SignupSupport className="sv-support-desktop" />
          </aside>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
